import { useEffect, useState, useCallback } from 'react'
import { DropZone } from '@components/Upload/DropZone'
import { CameraModal } from '@components/Camera/CameraModal'
import { clientSigningApi } from '@services/api'
import styles from './ClientSigning.module.css'

interface ClientIdentityUploadProps {
  token: string
  clientName: string
  onComplete: () => void
}

type SideKey = 'fronte' | 'retro'

interface SideState {
  uploaded: boolean
  isUploading: boolean
  error: string | null
}

const ACCEPT_IMAGES = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
}

export function ClientIdentityUpload({
  token,
  clientName,
  onComplete,
}: ClientIdentityUploadProps) {
  const [loading, setLoading] = useState(true)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [fronte, setFronte] = useState<SideState>({ uploaded: false, isUploading: false, error: null })
  const [retro, setRetro] = useState<SideState>({ uploaded: false, isUploading: false, error: null })
  const [showCamera, setShowCamera] = useState(false)
  const [cameraTarget, setCameraTarget] = useState<SideKey>('fronte')

  const refreshStatus = useCallback(async () => {
    try {
      const status = await clientSigningApi.getIdentityStatus(token)
      setFronte((s) => ({ ...s, uploaded: status.has_fronte }))
      setRetro((s) => ({ ...s, uploaded: status.has_retro }))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore caricamento stato'
      setStatusError(message)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    refreshStatus()
  }, [refreshStatus])

  const setSideUploading = (side: SideKey, uploading: boolean, error: string | null = null) => {
    if (side === 'fronte') {
      setFronte((s) => ({ ...s, isUploading: uploading, error }))
    } else {
      setRetro((s) => ({ ...s, isUploading: uploading, error }))
    }
  }

  const handleUpload = async (side: SideKey, blob: Blob, filename = `identity-${side}.jpg`) => {
    setSideUploading(side, true, null)
    try {
      const result = await clientSigningApi.uploadIdentity(token, blob, side, filename)
      const uploaded = side === 'fronte' ? result.has_fronte : result.has_retro
      if (side === 'fronte') {
        setFronte({ uploaded, isUploading: false, error: null })
      } else {
        setRetro({ uploaded, isUploading: false, error: null })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore upload'
      setSideUploading(side, false, message)
    }
  }

  const handleFiles = (side: SideKey) => (files: File[]) => {
    const file = files[0]
    if (!file) return
    handleUpload(side, file, file.name)
  }

  const handleCameraOpen = (side: SideKey) => {
    setCameraTarget(side)
    setShowCamera(true)
  }

  const handleCameraCapture = async (blobs: Blob[]) => {
    setShowCamera(false)
    const blob = blobs[0]
    if (!blob) return
    await handleUpload(cameraTarget, blob, `identity-${cameraTarget}.jpg`)
  }

  const allUploaded = fronte.uploaded && retro.uploaded

  if (loading) {
    return (
      <div className={styles.identityCard}>
        <div className={styles.identityHeader}>
          <i className="bi bi-person-vcard" />
          <h2>Documento d'Identità</h2>
        </div>
        <p>Caricamento…</p>
      </div>
    )
  }

  if (statusError) {
    return (
      <div className={styles.identityCard}>
        <div className={styles.identityHeader}>
          <i className="bi bi-exclamation-triangle" />
          <h2>Errore</h2>
        </div>
        <p>{statusError}</p>
        <button type="button" className={styles.primaryButton} onClick={() => { setStatusError(null); setLoading(true); refreshStatus() }}>
          Riprova
        </button>
      </div>
    )
  }

  return (
    <div className={styles.identityCard}>
      <div className={styles.identityHeader}>
        <i className="bi bi-person-vcard" />
        <div>
          <h2>Carica il tuo documento</h2>
          <p>
            Ciao {clientName}. Per completare la firma, carica fronte e retro del tuo documento d'identità
            (carta d'identità, patente o passaporto). Formato JPG/PNG, max 5MB per immagine.
          </p>
        </div>
      </div>

      <div className={styles.identitySides}>
        {(['fronte', 'retro'] as const).map((side) => {
          const state = side === 'fronte' ? fronte : retro
          return (
            <div key={side} className={styles.identitySide}>
              <div className={styles.identitySideHeader}>
                <span className={styles.identitySideLabel}>
                  {side === 'fronte' ? 'Fronte' : 'Retro'}
                </span>
                {state.uploaded ? (
                  <span className={styles.identityBadgeOk}>
                    <i className="bi bi-check-circle-fill" />
                    Caricato
                  </span>
                ) : (
                  <span className={styles.identityBadgeMissing}>
                    <i className="bi bi-exclamation-circle" />
                    Mancante
                  </span>
                )}
              </div>

              <DropZone
                onFilesSelected={handleFiles(side)}
                accept={ACCEPT_IMAGES}
                maxSize={5 * 1024 * 1024}
                disabled={state.isUploading}
                multiple={false}
                label={state.uploaded ? `${side === 'fronte' ? 'Fronte' : 'Retro'} caricato. Sostituisci con un nuovo file` : `Trascina qui ${side === 'fronte' ? 'il fronte' : 'il retro'}`}
                dragLabel="Rilascia qui il file"
                compact
              />

              <button
                type="button"
                className={styles.cameraButton}
                onClick={() => handleCameraOpen(side)}
                disabled={state.isUploading}
              >
                <i className="bi bi-camera" />
                {state.uploaded ? 'Riscatta foto' : 'Scatta foto'}
              </button>

              {state.isUploading && (
                <p className={styles.uploadStatus}>
                  <i className="bi bi-arrow-repeat" />
                  Caricamento in corso…
                </p>
              )}

              {state.error && (
                <p className={styles.uploadError}>{state.error}</p>
              )}
            </div>
          )
        })}
      </div>

      <button
        type="button"
        className={styles.primaryButton}
        onClick={onComplete}
        disabled={!allUploaded || fronte.isUploading || retro.isUploading}
      >
        <i className="bi bi-check2-circle" />
        Continua con la firma
      </button>

      <CameraModal
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={handleCameraCapture}
        maxPhotos={1}
      />
    </div>
  )
}
